import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { ConsumptionData, IVouchersHttpService } from '../interfaces';
import { firstValueFrom } from 'rxjs';
import {
  VoucherCancellationResponseBack,
  VoucherConsumptionResponseBack,
  VoucherHistoryResponseBack,
  VoucherResponseBack,
  SessionsResponseBack,
  VoucherBySerialNumberResponseBack,
  VoucherBySerialNumberSearchParams,
  GiftCardResponseBack,
} from '../interfaces/types';
import { API_DEFAULT_PARAMS } from '../constants';
import { ERROR_ACTION_KEY } from '../../../tokens';

@Injectable()
export class VouchersHttpService implements IVouchersHttpService {
  constructor(protected httpClient: HttpClient) {}

  getVoucher(country: string, numCard: string, skipErrorInterceptor = false): Promise<VoucherResponseBack> {
    return firstValueFrom(
      this.httpClient.get<VoucherResponseBack>(`/vouchers/${numCard}`, {
        params: { country },
        headers: skipErrorInterceptor ? new HttpHeaders().set('Skip-Error-Interceptor', 'true') : undefined,
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.voucher_details'),
      }),
    );
  }

  getVoucherHistory(country: string, numCard: string): Promise<VoucherHistoryResponseBack> {
    return firstValueFrom(
      this.httpClient.get<VoucherHistoryResponseBack>(`/vouchers/${numCard}/history`, {
        params: { country },
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.voucher_history'),
      }),
    );
  }

  getVoucherSessions(country: string, numCard: string): Promise<SessionsResponseBack> {
    return firstValueFrom(
      this.httpClient.get<SessionsResponseBack>(`/vouchers/${numCard}/sessions`, {
        params: { country },
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.voucher_sessions'),
      }),
    );
  }

  consumeVoucher(country: string, numCard: string, data: ConsumptionData): Promise<VoucherConsumptionResponseBack> {
    const params = { country, ...API_DEFAULT_PARAMS };

    return firstValueFrom(
      this.httpClient.post<VoucherConsumptionResponseBack>(`/vouchers/${numCard}/consume`, data, {
        params,
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.validate_order'),
      }),
    );
  }

  cancelVoucherOperation(
    country: string,
    numCard: string,
    consumeTransactionId: number,
    transactionChannel: string,
  ): Promise<VoucherCancellationResponseBack> {
    const params = { country, consumeTransactionId, transactionChannel, posSystem: API_DEFAULT_PARAMS.posSystem };

    return firstValueFrom(
      this.httpClient.post<VoucherCancellationResponseBack>(
        `/vouchers/${numCard}/cancel`,
        {},
        {
          params,
          context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.cancel_order'),
        },
      ),
    );
  }

  getVoucherBySerialNumber(
    country: string,
    type: string,
    serialNumberStart: number,
    serialNumberEnd?: number,
  ): Promise<VoucherBySerialNumberResponseBack> {
    let params: VoucherBySerialNumberSearchParams = { country, type, serialNumberStart };
    if (serialNumberEnd !== undefined) {
      params = { ...params, serialNumberEnd };
    }
    return firstValueFrom(
      this.httpClient.get<VoucherBySerialNumberResponseBack>(`/vouchers`, {
        params,
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.serial_search'),
      }),
    );
  }

  getGiftCard(country: string, numCard: string, pin: number): Promise<GiftCardResponseBack> {
    return firstValueFrom(
      this.httpClient.get<VoucherResponseBack>(`/gift-card/${numCard}`, {
        params: { country, pin },
        context: new HttpContext().set(ERROR_ACTION_KEY, 'errors.actions.gift_card_details'),
      }),
    );
  }
}
